import { ArrowBackIcon, Box, UIButton, useMediaQuery } from '../../ui'
import type { MouseEvent } from 'react'

interface FooterProps {
    handleBack: () => void
    shouldApplyTopBorder?: boolean,
    shouldApplyBackgroundColor?: boolean,
    shouldBackDisabled: boolean,
    nextButtonText: string,
    formId?: string,
    nextButtonLoading?: boolean,
    nextButtonDisabled?: boolean,
    // Optional secondary button (e.g. Skip)
    showSecondaryButton?: boolean,
    secondaryButtonText?: string,
    onSecondaryClick?: () => void,
    secondaryButtonLoading?: boolean,
    // Optional sx override for the secondary button (e.g. budget edit "Cancel")
    secondaryButtonSx?: object,
}

function Footer({ handleBack, shouldApplyTopBorder = true, shouldApplyBackgroundColor = true, shouldBackDisabled, nextButtonText, formId = 'onboarding-form', nextButtonLoading = false, nextButtonDisabled = false, showSecondaryButton = false, secondaryButtonText = 'Skip', onSecondaryClick, secondaryButtonLoading = false, secondaryButtonSx = {} }: FooterProps) {
  const isMobile = useMediaQuery('(max-width:900px)')
  const showBack = !shouldBackDisabled
  const makePrimaryFullWidth = isMobile && !showBack && !showSecondaryButton

  /**
   * iOS/WebKit reliability: when a submit button lives outside the <form> and uses
   * the HTML `form="..."` attribute, some environments (notably iOS Chrome/Safari
   * in modal/portal layouts) may not trigger the form submit at all.
   *
   * This helper forces a submit via `requestSubmit()` (best) with a safe fallback.
   */
  const submitFormById = (id?: string): boolean => {
    if (!id) return false
    if (typeof document === 'undefined') return false

    const el = document.getElementById(id)
    if (!el) return false

    const form = el as HTMLFormElement
    const anyForm = form as unknown as { requestSubmit?: () => void }

    if (typeof anyForm.requestSubmit === 'function') {
      anyForm.requestSubmit()
      return true
    }

    // Fallback for older WebKit: click a temporary hidden submit button.
    const tmp = document.createElement('button')
    tmp.type = 'submit'
    tmp.style.display = 'none'
    form.appendChild(tmp)
    tmp.click()
    form.removeChild(tmp)
    return true
  }

  return (
    <Box data-testid="components-on-boarding-footer-container-action-1" sx={{ 
        display: 'flex',
        justifyContent: showBack ? 'space-between' : 'flex-end',
        alignItems: 'center',
        p: 1,
        backgroundColor: shouldApplyBackgroundColor ? '#fff' : 'none',
        borderTop: shouldApplyTopBorder ? '1px solid #E0E0E0' : 'none'
      }}>
        {/* Back Button (hidden when disabled) */}
        {showBack && (
          <UIButton data-testid="components-on-boarding-footer-btn-handle-back"
            variant="outlined"
            size="large"
            onClick={handleBack}
            disabled={nextButtonLoading}
            sx={{
              minWidth: '80px',
              py: 1.5,
              borderColor: '#E0E0E0',
              color: '#666',
              borderRadius: 2,
              fontSize: '16px',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#BDBDBD',
                backgroundColor: '#F5F5F5'
              },
              '&:disabled': {
                borderColor: '#E0E0E0',
                color: '#BDBDBD'
              }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </UIButton>
        )}

        {/* Right side actions */}
        <Box data-testid="components-on-boarding-footer-container-action-2" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5, 
          justifyContent: 'flex-end',
          // Expand only on mobile when making primary full width
          flex: makePrimaryFullWidth ? 1 : 'unset',
          width: makePrimaryFullWidth ? '100%' : 'auto'
        }}>
          {showSecondaryButton && (
            <UIButton data-testid="components-on-boarding-footer-btn-on-secondary-click"
              variant="outlined"
              size="large"
              onClick={onSecondaryClick}
              disabled={nextButtonLoading || secondaryButtonLoading}
              loading={secondaryButtonLoading}
              loadingText={secondaryButtonText}
              sx={{
                minWidth: '140px',
                py: 1.5,
                borderColor: '#E0E0E0',
                color: '#033F44',
                borderRadius: 2,
                fontSize: '16px',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#BDBDBD',
                  backgroundColor: '#F5F5F5'
                },
                ...secondaryButtonSx
              }}
            >
              {secondaryButtonText}
            </UIButton>
          )}

          {/* Continue Button */}
          <UIButton data-testid="components-on-boarding-footer-btn-action"
            variant="contained"
            size="large"
            type="submit"
            form={formId}
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              // Force-submit for iOS/WebKit where `form={formId}` can be flaky in dialogs.
              const didSubmit = submitFormById(formId)
              if (didSubmit) {
                // Avoid double-submit in environments where the native form attribute works.
                e.preventDefault()
              }
            }}
            loading={nextButtonLoading}
            loadingText={nextButtonText}
            disabled={nextButtonDisabled}
            fullWidth={makePrimaryFullWidth}
            sx={{
              minWidth: makePrimaryFullWidth ? 'unset' : '200px',
              py: 1.5,
              backgroundColor: '#004D44',
              borderRadius: 2,
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#004D44',
                boxShadow: 'none',
                border: '0.5px solid #004D44'
              },
              '&:disabled': {
                backgroundColor: '#E8EFEE',
                color: '#00243E'
              }
            }}
          >
            {nextButtonText}
          </UIButton>
        </Box>
      </Box>
  )
}

export default Footer